import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Star, ImagePlus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useLang } from "@/i18n/LanguageContext";

const fileToDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const max = 1280;
        let { width, height } = img;
        if (width > max || height > max) {
          const s = Math.min(max / width, max / height);
          width = Math.round(width * s);
          height = Math.round(height * s);
        }
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        canvas.getContext("2d").drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL("image/jpeg", 0.82));
      };
      img.onerror = reject;
      img.src = reader.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const ReviewModal = ({ open, onClose, onCreated }) => {
  const { t } = useLang();
  const [rating, setRating] = useState(5);
  const [hover, setHover] = useState(0);
  const [text, setText] = useState("");
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);

  const reset = () => {
    setRating(5);
    setText("");
    setImages([]);
  };

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []).slice(0, 5 - images.length);
    try {
      const urls = await Promise.all(files.map(fileToDataUrl));
      setImages((prev) => [...prev, ...urls].slice(0, 5));
    } catch {
      toast.error(t("reviews.form.error"));
    }
    e.target.value = "";
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/reviews", { rating, text, images });
      toast.success(t("reviews.form.success"));
      reset();
      onClose();
      onCreated?.(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="review-modal"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[150] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.95, y: 20 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.95, y: 20 }}
            transition={{ duration: 0.25 }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-lg bg-card border border-gold/20 rounded-sm p-8 md:p-10 max-h-[92vh] overflow-y-auto"
          >
            <button
              onClick={onClose}
              className="absolute top-4 right-4 text-muted-foreground hover:text-gold transition-colors"
              data-testid="review-close"
            >
              <X size={22} />
            </button>

            <h3 className="font-serif text-3xl text-foreground">{t("reviews.form.title")}</h3>

            <form onSubmit={submit} className="mt-6 space-y-6">
              <div>
                <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2">
                  {t("reviews.form.rating")}
                </div>
                <div className="flex gap-1.5" data-testid="rating-stars">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button
                      key={n}
                      type="button"
                      data-testid={`star-${n}`}
                      onMouseEnter={() => setHover(n)}
                      onMouseLeave={() => setHover(0)}
                      onClick={() => setRating(n)}
                    >
                      <Star
                        size={30}
                        className={`transition-colors ${
                          n <= (hover || rating) ? "text-gold fill-gold" : "text-muted-foreground"
                        }`}
                        fill={n <= (hover || rating) ? "currentColor" : "none"}
                      />
                    </button>
                  ))}
                </div>
              </div>

              <textarea
                data-testid="review-text"
                required
                rows={4}
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder={t("reviews.form.textPlaceholder")}
                className="w-full bg-transparent border border-border focus:border-gold text-foreground p-3 placeholder:text-muted-foreground transition-colors outline-none font-sans resize-none rounded-sm"
              />

              <div>
                <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-3">
                  {t("reviews.form.photos")}
                </div>
                <div className="flex flex-wrap gap-3">
                  {images.map((src, i) => (
                    <div key={i} className="relative w-20 h-20 rounded-sm overflow-hidden border border-border">
                      <img src={src} alt="" className="w-full h-full object-cover" />
                      <button
                        type="button"
                        onClick={() => setImages((prev) => prev.filter((_, idx) => idx !== i))}
                        className="absolute top-0.5 right-0.5 bg-black/70 text-white rounded-full p-1"
                      >
                        <Trash2 size={12} />
                      </button>
                    </div>
                  ))}
                  {images.length < 5 && (
                    <label
                      data-testid="review-add-photo"
                      className="w-20 h-20 flex flex-col items-center justify-center gap-1 border border-dashed border-border hover:border-gold/60 rounded-sm cursor-pointer text-muted-foreground hover:text-gold transition-colors"
                    >
                      <ImagePlus size={20} />
                      <input type="file" accept="image/*" multiple onChange={onFiles} className="hidden" />
                    </label>
                  )}
                </div>
              </div>

              <button
                data-testid="review-submit"
                type="submit"
                disabled={loading}
                className="w-full bg-gold text-black font-sans font-semibold text-sm uppercase tracking-[0.15em] py-3.5 rounded-sm hover:bg-gold-deep transition-colors disabled:opacity-60"
              >
                {loading ? t("reviews.form.sending") : t("reviews.form.submit")}
              </button>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ReviewModal;
